import type { LucideIcon } from 'lucide-react';
import React from 'react';

interface AnalyticsPdfReportHeaderProps {
  icon: LucideIcon;
  title: string;
  metaItems: Array<{ label: string; value: string }>;
}

const ANALYTICS_REPORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export const formatAnalyticsReportDate = (value: string): string => {
  const normalizedValue = value.trim();
  if (!normalizedValue) return 'Not set';

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizedValue);
  if (!match) return 'Not set';

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return 'Not set';
  }

  return ANALYTICS_REPORT_DATE_FORMATTER.format(date);
};

export const AnalyticsPdfReportHeader: React.FC<AnalyticsPdfReportHeaderProps> = ({
  icon: Icon,
  title,
  metaItems,
}) => (
  // Hidden in the UI, then shown during PDF capture by showExportOnlyContent().
  <header
    data-export-visible="flex"
    data-export-pdf-block
    className="hidden flex-col items-start gap-3 rounded-lg bg-card p-4"
  >
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase text-muted-foreground">Analytics</p>
        <h2 className="mt-0.5 text-xl font-semibold leading-7 text-foreground">{title}</h2>
      </div>
    </div>
    <div className="flex flex-wrap justify-start gap-2 text-left text-xs text-muted-foreground">
      {metaItems.map(item => (
        <span
          key={item.label}
          className="inline-block max-w-full whitespace-normal break-words rounded-md border bg-background px-2 py-1"
        >
          {item.label}: {item.value}
        </span>
      ))}
    </div>
  </header>
);
